

































































































import {
  defineComponent,
  PropType,
  computed,
  ref,
  onMounted,
  watch,
} from "@nuxtjs/composition-api";
import { ProductParent } from "~/modules/GraphQL/types";
import EditItemCartAvaiableOptions from "../EditItemCartAvaiableOptions.vue";
import useProductStock from "~/modules/catalog/product/composables/useProductStock";
import type { SelectedOption } from "~/modules/catalog/types";
import { useCartView } from "~/modules/checkout/composables/useCartView";
import { useUiNotification } from "~/composables";
import { decodeUid } from "~/helpers";

import {
  getVariantName,
  getColorLabel,
  getSizeLabel,
  getPrice,
} from "~/modules/checkout/helpers";
import { getFirstImageByVariants, setSizeProductList } from "~/modules/catalog/category/helpers";
import { MapProductVariant } from "~/modules/catalog/category/composables/useElasticSearch/types";

export default defineComponent({
  name: "FreeGiftListModal",
  components: { EditItemCartAvaiableOptions },
  props: {
    product: {
      type: Object as PropType<ProductParent>,
      default: null,
    },
    availableOptions: {
      type: Object as PropType<{
        color: any[];
        size: any[];
      }>,
      default: () => [],
    },
    selectedOptions: {
      type: Object as PropType<SelectedOption>,
      default: () => ({
        color: null,
        size: null,
      }),
    },
    variants: {
      type: Array as PropType<MapProductVariant[]>,
      default: () => [],
    },
    needMoney: {
      type: Number,
      default: 0,
    },
    needQty: {
      type: Number,
      default: 0,
    },
    ampromoRuleId: {
      type: String as PropType<string | number>,
      default: null,
    },
  },

  setup(props, { emit }) {
    const cartView = useCartView();
    const { getProductListStock } = useProductStock();
    const { send: sendNotification } = useUiNotification();
    const parentSku = computed(() => {
      return props.product?.sku || "";
    });

    const showColorAlert = ref(false);
    const showSizeAlert = ref(false);

    const skuList = computed(() => {
      return props.variants.map((variant) => variant?.sku);
    });

    const checkCart = ref(false);
    const dataCheckOptionInstock = ref([]);
    const totalCount = ref(0);
    const selectedOption = ref({
      size: null,
      color: null,
    });

    const updateSelectedOption = (attributeOption: any) => {
      if (attributeOption.type === "color") {
        selectedOption.value.color = attributeOption;
      } else if (attributeOption.type === "size") {
        selectedOption.value.size = attributeOption;
      }
    };

    const checkStockBySkus = async () => {
      const paramSkus = skuList.value
        .map((sku) => encodeURIComponent(sku))
        .join(",");
      dataCheckOptionInstock.value = await getProductListStock(paramSkus);
      checkoption();
    };

    const findMatchingVariant = (selectedOption: SelectedOption) => {
      const selectedColor = selectedOption.color;
      const selectedSize = selectedOption.size;
      const configurableOpt = props.product?.configurable_options_map || [];
      const hasColorAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "color"
      );
      const hasSizeAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "size"
      );

      return (
        props.variants.find((variant) => {
          const colorMatch =
            !hasColorAttribute ||
            !selectedColor ||
            variant.color?.toString() === selectedColor?.value_index?.toString();

          const sizeMatch =
            !hasSizeAttribute ||
            !selectedSize ||
            variant.size?.toString() === selectedSize?.value_index?.toString();

          if (hasColorAttribute && hasSizeAttribute) {
            return colorMatch && sizeMatch;
          }
          if (hasColorAttribute && !hasSizeAttribute) {
            return colorMatch;
          }
          if (!hasColorAttribute && hasSizeAttribute) {
            return colorMatch;
          }
        }) || null
      );
    };

    const findMatchingSku = (selectedOption: SelectedOption) => {
      return findMatchingVariant(selectedOption)?.sku || null;
    };

    const checkoption = () => {
      if (
        !dataCheckOptionInstock.value ||
        dataCheckOptionInstock.value.length < 1
      ) {
        checkCart.value = false;
        return;
      }

      let sku;
      const { color, size } = selectedOption.value;
      if (!color && !size) {
        sku = parentSku.value;
      } else if (color && !size) {
        sku = `${parentSku.value}-${color.label}`;
      } else if (color && size) {
        sku = findMatchingSku(selectedOption.value);
      }
      if (sku) {
        checkSkuInStock(sku);
      }
    };

    const checkSkuInStock = (sku: string) => {
      if (dataCheckOptionInstock.value[sku]) {
        checkCart.value = dataCheckOptionInstock.value[sku]?.is_in_stock;
        totalCount.value = dataCheckOptionInstock.value[sku]?.qty;
      } else {
        totalCount.value = 0;
        for (const key in dataCheckOptionInstock.value) {
          if (dataCheckOptionInstock.value[key].sku.includes(sku)) {
            totalCount.value =
              totalCount.value + dataCheckOptionInstock.value[key]?.qty;
          }
        }

        if (totalCount.value > 0) {
          checkCart.value = true;
        } else {
          checkCart.value = false;
        }
      }
    };

    const getProductImages = computed(() => {
      if (selectedOption.value.color) {
        return getFirstImageByVariants(
          selectedOption.value.color,
          props.variants
        );
      }

       return props.product && props.product.media_gallery && props.product.media_gallery.length > 0
        ? setSizeProductList(props.product.media_gallery?.[0].image)
        : setSizeProductList(props.product?.thumbnail_url);
    });

    onMounted(async () => {
      selectedOption.value.color = props.selectedOptions.color || props.availableOptions?.color?.[0];

      await checkStockBySkus();
    });

    watch(
      () => selectedOption.value,
      () => {
        checkoption();
      },
      { deep: true }
    );

    watch(
      () => selectedOption.value.color,
      (newVal, oldVal) => {
        if (!oldVal || newVal.uid === oldVal.uid) {
          return;
        }
        selectedOption.value.size = null;
      }
    );

    const closeDetailModal = () => {
      emit("close");
    };

    const isLoadingAddToCart = ref(false);
    const onConfirm = async () => {
      if (props.needMoney && props.needMoney > 0) {
        sendNotification({
          id: Symbol("free_gift"),
          type: "info",
          message:
            "Bạn cần mua hàng tối thiểu " +
            props.needMoney +
            "đ để nhận quà tặng",
          icon: "info",
          persist: false,
          title: "free_gift",
        });
        return;
      }
      if (props.needQty && props.needQty > 0) {
        sendNotification({
          id: Symbol("free_gift"),
          type: "info",
          message:
            "Bạn cần mua thêm tối thiểu " +
            props.needQty +
            " sản phẩm nữa để nhận quà tặng",
          icon: "info",
          persist: false,
          title: "free_gift",
        });
        return;
      }
      try {
        isLoadingAddToCart.value = true;
        const freeGiftRuleIds =
          cartView?.extensionAttributes.value?.free_gift_rule_ids || "";
        const sku = findMatchingSku(selectedOption.value);

        const productId = props.variants.find(
          (variant) => variant.sku === sku
        )?.id;
        const productUid = props.variants.find(
          (variant) => variant.sku === sku
        )?.uid;

        const param = {
          gift_data: {
            product_id: productId || decodeUid(productUid),
            qty: 1,
            ampromo_rule_id: props.ampromoRuleId,
          },
        };
        await cartView.addToCartFreeGift(param);
        emit("closeSuscess");
      } catch (error) {
        console.error("Error adding to cart:", error);
      } finally {
        isLoadingAddToCart.value = false;
      }
    };

    return {
      closeDetailModal,
      onConfirm,
      parentSku,
      getVariantName,
      getColorLabel,
      getSizeLabel,
      getPrice,
      selectedOption,
      updateSelectedOption,
      checkStockBySkus,
      checkCart,
      checkoption,
      showColorAlert,
      showSizeAlert,
      dataCheckOptionInstock,
      getProductImages,
      findMatchingSku,
      isLoadingAddToCart,
    };
  },
});
